import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { _getStatesBySiteID } from '../shared/httpservice/apis/state';
import { _getZonesBySiteID } from '../shared/httpservice/apis/zone';
import { _createLocation, _deleteLocation, _getLocationsBySiteID, _updateLocation, } from '../shared/httpservice/apis/location';
import { _createCategory, _deleteCategory, _getCategoriessBySiteID, _updateCategory, _getCategoriesIcon, } from '../shared/httpservice/apis/category';
import { _createLayerBySiteID, _getLayersBySiteID, _updateLayerBySiteID } from '../shared/httpservice/apis/layers';
import { _deleteEventImage, _uploadCategoryImage, _uploadEventImage, _uploadLocationImage, _uploadMapObject, } from '../shared/httpservice/apis/image';
import { _getShape } from '../shared/httpservice/apis/shapes';
import { _getConnectionsBySiteID, _createConnection } from '../shared/httpservice/apis/connections';
import { _createEvent, _deleteEvent, _getEventsBySiteID, _updateEvent } from '../shared/httpservice/apis/event';
const createInitialState = () => ({
    loading: false,
    response: null,
    hasError: false,
    error: {},
});
const initialState = {
    locationStates: createInitialState(),
    locationZones: createInitialState(),
    locationSiblings: createInitialState(),
    locationCategories: createInitialState(),
    events: createInitialState(),
    createEvent: createInitialState(),
    updateEvent: createInitialState(),
    deleteEvent: createInitialState(),
    siteLayers: createInitialState(),
    createLayer: createInitialState(),
    updateLayer: createInitialState(),
    createLocation: createInitialState(),
    updateLocation: createInitialState(),
    deleteLocation: createInitialState(),
    createCategory: createInitialState(),
    updateCategory: createInitialState(),
    deleteCategory: createInitialState(),
    floorShapes: createInitialState(),
    connections: createInitialState(),
    createConnections: createInitialState(),
    categoryIcons: createInitialState()
};
// Async thunk for fetching location types
export const fetchLocationStates = createAsyncThunk('location/fetchLocationStates', async (siteId, { rejectWithValue }) => {
    const pageSize = 50;
    try {
        const firstPageResponse = await _getStatesBySiteID(siteId, 0, pageSize);
        const { totalPages, content } = firstPageResponse;
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => _getStatesBySiteID(siteId, i + 1, pageSize));
        const remainingPagesResponses = await Promise.all(remainingPages);
        const allData = [...content, ...remainingPagesResponses.flatMap((response) => response.content)];
        return allData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Async thunk for fetching location types
export const fetchLocationZones = createAsyncThunk('location/fetchLocationZones', async (siteId, { rejectWithValue }) => {
    const pageSize = 50;
    try {
        const firstPageResponse = await _getZonesBySiteID(siteId, 0, pageSize);
        const { totalPages, content } = firstPageResponse;
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => _getStatesBySiteID(siteId, i + 1, pageSize));
        const remainingPagesResponses = await Promise.all(remainingPages);
        const allData = [...content, ...remainingPagesResponses.flatMap((response) => response.content)];
        //
        return allData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const fetchLocationSiblings = createAsyncThunk('location/fetchLocationSiblings', async (siteId, { rejectWithValue }) => {
    const pageSize = 50;
    try {
        const firstPageResponse = await _getLocationsBySiteID(siteId, 0, pageSize);
        const { totalPages, content } = firstPageResponse;
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => _getLocationsBySiteID(siteId, i + 1, pageSize));
        const remainingPagesResponses = await Promise.all(remainingPages);
        const allData = [...content, ...remainingPagesResponses.flatMap((response) => response.content)];
        return allData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const fetchLocationCategories = createAsyncThunk('location/fetchLocationCategories', async (siteId, { rejectWithValue }) => {
    const pageSize = 50;
    try {
        const firstPageResponse = await _getCategoriessBySiteID(siteId, 0, pageSize);
        const { totalPages, content } = firstPageResponse;
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => _getCategoriessBySiteID(siteId, i + 1, pageSize));
        const remainingPagesResponses = await Promise.all(remainingPages);
        const allData = [...content, ...remainingPagesResponses.flatMap((response) => response.content)];
        return allData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const groupByFloorId = (layers) => {
    return layers.reduce((acc, layer) => {
        const { floorId } = layer;
        if (!acc[floorId]) {
            acc[floorId] = [];
        }
        acc[floorId].push(layer);
        return acc;
    }, {});
};
export const fetchSiteLayers = createAsyncThunk('site/fetchLayers', async (siteId, { rejectWithValue }) => {
    const pageSize = 50;
    try {
        const firstPageResponse = await _getLayersBySiteID(siteId, 0, pageSize);
        const { totalPages, content } = firstPageResponse;
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => _getLayersBySiteID(siteId, i + 1, pageSize));
        const remainingPagesResponses = await Promise.all(remainingPages);
        const allData = [...content, ...remainingPagesResponses.flatMap((response) => response.content)];
        const groupedLayers = groupByFloorId(allData);
        return groupedLayers;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const createSiteLayers = createAsyncThunk('site/createLayer', async ({ siteId, floorId, name }, { rejectWithValue }) => {
    try {
        const responseData = await _createLayerBySiteID(siteId, floorId, name);
        //
        return responseData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const updateSiteLayers = createAsyncThunk('site/updateLayer', async ({ siteId, floorId, name }, { rejectWithValue }) => {
    try {
        const responseData = await _updateLayerBySiteID(siteId, floorId, name);
        return responseData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const createLocationAction = createAsyncThunk('location/create', async ({ data, fileUpload, objectUpload, scale }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _createLocation(data);
        if (responseData?.locationId) {
            if (fileUpload) {
                let promise = [];
                Object.entries(fileUpload).forEach(([key, value]) => {
                    if (value) {
                        promise.push(_uploadLocationImage(data.siteId, responseData.locationId, key.toUpperCase(), { image: value }));
                    }
                });
                await Promise.all(promise);
                if (objectUpload) {
                    console.log(objectUpload, "object upload");
                    const formData = new FormData();
                    formData.append('file', objectUpload, 'mapObject.zip');
                    for (const [key, value] of formData.entries()) {
                        console.log(key, value);
                    }
                    await _uploadMapObject(data.siteId, responseData.locationId, scale, formData);
                }
            }
            dispatch(fetchLocationSiblings(data.siteId));
            //
            return responseData;
        }
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const updateLocation = createAsyncThunk('location/update', async ({ data, siteId, locationId, fileUpload, }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _updateLocation(siteId, locationId, data);
        if (fileUpload) {
            let promise = [];
            Object.entries(fileUpload).forEach(([key, value]) => {
                if (value) {
                    promise.push(_uploadLocationImage(siteId, locationId, key.toUpperCase(), {
                        image: value,
                    }));
                }
            });
            await Promise.all(promise);
        }
        dispatch(fetchLocationSiblings(siteId));
        //
        return responseData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const deleteLocation = createAsyncThunk('location/delete', async ({ siteId, locationId }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _deleteLocation(siteId, locationId);
        dispatch(fetchLocationSiblings(siteId));
        //
        return responseData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const createCategoryAction = createAsyncThunk('category/create', async ({ data, fileUpload }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _createCategory(data);
        if (responseData?.categoryId) {
            if (fileUpload) {
                let promise = [];
                Object.entries(fileUpload).forEach(([key, value]) => {
                    if (value) {
                        promise.push(_uploadCategoryImage(data.siteId, responseData.categoryId, {
                            image: value,
                        }));
                    }
                });
                await Promise.all(promise);
            }
        }
        dispatch(fetchLocationCategories(data.siteId));
        return responseData;
    }
    catch (error) {
        console.error({ error });
        return rejectWithValue(error?.response?.data || error);
    }
});
export const updateCategoryAction = createAsyncThunk('category/update', async ({ data, siteId, categoryId, fileUpload, }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _updateCategory(siteId, categoryId, data);
        if (fileUpload) {
            let promise = [];
            Object.entries(fileUpload).forEach(([key, value]) => {
                if (value) {
                    promise.push(_uploadCategoryImage(siteId, categoryId, { image: value }));
                }
            });
            await Promise.all(promise);
        }
        dispatch(fetchLocationCategories(siteId));
        return responseData;
    }
    catch (error) {
        console.error({ error });
        return rejectWithValue(error?.response?.data || error);
    }
});
export const deleteCategory = createAsyncThunk('category/delete', async ({ siteId, categoryId }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _deleteCategory(siteId, categoryId);
        dispatch(fetchLocationCategories(siteId));
        //
        return responseData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getFloorShapeAction = createAsyncThunk('editor/floorShapes', async ({ siteId, floorId }, { rejectWithValue }) => {
    const pageSize = 50;
    try {
        const firstPageResponse = await _getShape(siteId, floorId, 0, pageSize);
        const { totalPages, content } = firstPageResponse;
        let allData = [...content];
        for (let i = 0; i <= totalPages - 1; i++) {
            try {
                const response = await _getShape(siteId, floorId, i + 1, pageSize);
                allData = [...allData, ...response?.content];
            }
            catch (error) { }
        }
        // const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) =>
        //   _getShape(siteId, floorId, i + 1, pageSize)
        // );
        // const remainingPagesResponses = await Promise.all(remainingPages);
        // const allData = [...content, ...remainingPagesResponses.flatMap((response) => response.content)];
        return allData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getConnectionsAction = createAsyncThunk('editor/connections', async (siteId, { rejectWithValue }) => {
    const pageSize = 50;
    try {
        const firstPageResponse = await _getConnectionsBySiteID(siteId, 0, pageSize);
        const { totalPages, content } = firstPageResponse;
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => _getConnectionsBySiteID(siteId, i + 1, pageSize));
        const remainingPagesResponses = await Promise.all(remainingPages);
        const allData = [...content, ...remainingPagesResponses.flatMap((response) => response.content)];
        return allData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const createConnectionAction = createAsyncThunk('editor/createConnections', async (data, { rejectWithValue }) => {
    try {
        const response = await _createConnection(data);
        return response;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getCategoriesIconAction = createAsyncThunk('editor/categoriesIcon', async (_, { rejectWithValue }) => {
    try {
        const response = await _getCategoriesIcon();
        return response;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Async thunk for fetching events
export const fetchEvents = createAsyncThunk('events/fetchEvents', async (siteId, { rejectWithValue }) => {
    const pageSize = 50;
    try {
        const firstPageResponse = await _getEventsBySiteID(siteId, 0, pageSize);
        const { totalPages, content } = firstPageResponse;
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => _getEventsBySiteID(siteId, i + 1, pageSize));
        const remainingPagesResponses = await Promise.all(remainingPages);
        const allData = [...content, ...remainingPagesResponses.flatMap((response) => response.content)];
        return allData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const createEventAction = createAsyncThunk('event/create', async ({ data, fileUpload }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _createEvent(data);
        if (responseData?.eventId) {
            if (fileUpload) {
                let promise = [];
                (fileUpload || []).map((it) => {
                    promise.push(_uploadEventImage(data.siteId, responseData.eventId, it));
                });
                await Promise.all(promise);
            }
        }
        dispatch(fetchEvents(data.siteId));
        return responseData;
    }
    catch (error) {
        console.error({ error });
        return rejectWithValue(error?.response?.data || error);
    }
});
export const updateEventAction = createAsyncThunk('event/update', async ({ data, siteId, eventId, fileUpload, fileDelete, }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _updateEvent(siteId, eventId, data);
        let promise = [];
        if (fileUpload) {
            // let promise: any = [];
            (fileUpload || []).map((it) => {
                promise.push(_uploadEventImage(data.siteId, responseData.eventId, it));
            });
        }
        // for (const it of fileUpload || []) {
        //   console.log('Fetching item:', it);
        //   try {
        //     await _uploadEventImage(data.siteId, responseData.eventId, it);
        //   } catch (error) {}
        // }
        if (fileDelete?.length) {
            promise.push(_deleteEventImage(siteId, eventId, { filePaths: fileDelete }));
        }
        try {
            await Promise.all(promise);
        }
        catch (error) {
            console.log('Promise all', { error });
        }
        dispatch(fetchEvents(siteId));
        return responseData;
    }
    catch (error) {
        console.error({ error });
        return rejectWithValue(error?.response?.data || error);
    }
});
export const deleteEvent = createAsyncThunk('event/delete', async ({ siteId, eventId }, { rejectWithValue, dispatch }) => {
    try {
        const responseData = await _deleteEvent(siteId, eventId);
        dispatch(fetchEvents(siteId));
        //
        return responseData;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Create the slice
const editorSlice = createSlice({
    name: 'editor',
    initialState,
    reducers: {
        resetLocationTypes: (state, action) => {
            state.locationStates = action.payload;
        },
        resetLocationZones: (state, action) => {
            state.locationZones = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLocationStates.pending, (state) => {
            state.locationStates.loading = true;
            state.locationStates.response = null;
            state.locationStates.hasError = false;
            state.locationStates.error = {};
        })
            .addCase(fetchLocationStates.fulfilled, (state, action) => {
            state.locationStates.loading = false;
            //@ts-ignore
            state.locationStates.response = action.payload;
            state.locationStates.hasError = false;
            state.locationStates.error = {};
        })
            .addCase(fetchLocationStates.rejected, (state, action) => {
            state.locationStates.loading = false;
            state.locationStates.hasError = true;
            state.locationStates.error = action.payload;
        })
            .addCase(fetchLocationZones.pending, (state) => {
            state.locationZones.loading = true;
            state.locationZones.response = null;
            state.locationZones.hasError = false;
            state.locationZones.error = {};
        })
            .addCase(fetchLocationZones.fulfilled, (state, action) => {
            state.locationZones.loading = false;
            //@ts-ignore
            state.locationZones.response = action.payload;
            state.locationZones.hasError = false;
            state.locationZones.error = {};
        })
            .addCase(fetchLocationZones.rejected, (state, action) => {
            state.locationZones.loading = false;
            state.locationZones.hasError = true;
            state.locationZones.error = action.payload;
        })
            .addCase(fetchLocationSiblings.pending, (state) => {
            state.locationSiblings.loading = true;
            state.locationSiblings.response = null;
            state.locationSiblings.hasError = false;
            state.locationSiblings.error = {};
        })
            .addCase(fetchLocationSiblings.fulfilled, (state, action) => {
            state.locationSiblings.loading = false;
            //@ts-ignore
            state.locationSiblings.response = action.payload;
            state.locationSiblings.hasError = false;
            state.locationSiblings.error = {};
        })
            .addCase(fetchLocationSiblings.rejected, (state, action) => {
            state.locationSiblings.loading = false;
            state.locationSiblings.hasError = true;
            state.locationSiblings.error = action.payload;
        })
            .addCase(fetchLocationCategories.pending, (state) => {
            state.locationCategories.loading = true;
            state.locationCategories.response = null;
            state.locationCategories.hasError = false;
            state.locationCategories.error = {};
        })
            .addCase(fetchLocationCategories.fulfilled, (state, action) => {
            state.locationCategories.loading = false;
            //@ts-ignore
            state.locationCategories.response = action.payload;
            state.locationCategories.hasError = false;
            state.locationCategories.error = {};
        })
            .addCase(fetchLocationCategories.rejected, (state, action) => {
            state.locationCategories.loading = false;
            state.locationCategories.hasError = true;
            state.locationCategories.error = action.payload;
        })
            .addCase(fetchSiteLayers.pending, (state) => {
            state.siteLayers.loading = true;
            state.siteLayers.response = null;
            state.siteLayers.hasError = false;
            state.siteLayers.error = {};
        })
            .addCase(fetchSiteLayers.fulfilled, (state, action) => {
            state.siteLayers.loading = false;
            //@ts-ignore
            state.siteLayers.response = action.payload;
            state.siteLayers.hasError = false;
            state.siteLayers.error = {};
        })
            .addCase(fetchSiteLayers.rejected, (state, action) => {
            state.siteLayers.loading = false;
            state.siteLayers.hasError = true;
            state.siteLayers.error = action.payload;
        })
            .addCase(createSiteLayers.pending, (state) => {
            state.createLayer.loading = true;
            state.createLayer.response = null;
            state.createLayer.hasError = false;
            state.createLayer.error = {};
        })
            .addCase(createSiteLayers.fulfilled, (state, action) => {
            state.createLayer.loading = false;
            //@ts-ignore
            state.createLayer.response = action.payload;
            state.createLayer.hasError = false;
            state.createLayer.error = {};
        })
            .addCase(createSiteLayers.rejected, (state, action) => {
            state.createLayer.loading = false;
            state.createLayer.hasError = true;
            state.createLayer.error = action.payload;
        })
            .addCase(updateSiteLayers.pending, (state) => {
            state.updateLayer.loading = true;
            state.updateLayer.response = null;
            state.updateLayer.hasError = false;
            state.updateLayer.error = {};
        })
            .addCase(updateSiteLayers.fulfilled, (state, action) => {
            state.updateLayer.loading = false;
            //@ts-ignore
            state.updateLayer.response = action.payload;
            state.updateLayer.hasError = false;
            state.updateLayer.error = {};
        })
            .addCase(updateSiteLayers.rejected, (state, action) => {
            state.updateLayer.loading = false;
            state.updateLayer.hasError = true;
            state.updateLayer.error = action.payload;
        })
            .addCase(createLocationAction.pending, (state) => {
            state.createLocation.loading = true;
            state.createLocation.response = null;
            state.createLocation.hasError = false;
            state.createLocation.error = {};
        })
            .addCase(createLocationAction.fulfilled, (state, action) => {
            state.createLocation.loading = false;
            //@ts-ignore
            state.createLocation.response = action.payload;
            state.createLocation.hasError = false;
            state.createLocation.error = {};
        })
            .addCase(createLocationAction.rejected, (state, action) => {
            state.createLocation.loading = false;
            state.createLocation.hasError = true;
            state.createLocation.error = action.payload;
        })
            .addCase(updateLocation.pending, (state) => {
            state.updateLocation.loading = true;
            state.updateLocation.response = null;
            state.updateLocation.hasError = false;
            state.updateLocation.error = {};
        })
            .addCase(updateLocation.fulfilled, (state, action) => {
            state.updateLocation.loading = false;
            //@ts-ignore
            state.updateLocation.response = action.payload;
            state.updateLocation.hasError = false;
            state.updateLocation.error = {};
        })
            .addCase(updateLocation.rejected, (state, action) => {
            state.updateLocation.loading = false;
            state.updateLocation.hasError = true;
            state.updateLocation.error = action.payload;
        })
            .addCase(deleteLocation.pending, (state) => {
            state.deleteLocation.loading = true;
            state.deleteLocation.response = null;
            state.deleteLocation.hasError = false;
            state.deleteLocation.error = {};
        })
            .addCase(deleteLocation.fulfilled, (state, action) => {
            state.deleteLocation.loading = false;
            // @ts-ignore
            state.deleteLocation.response = action.payload;
            state.deleteLocation.hasError = false;
            state.deleteLocation.error = {};
        })
            .addCase(deleteLocation.rejected, (state, action) => {
            state.deleteLocation.loading = false;
            state.deleteLocation.hasError = true;
            state.deleteLocation.error = action.payload;
        })
            .addCase(createCategoryAction.pending, (state) => {
            state.createCategory.loading = true;
            state.createCategory.response = null;
            state.createCategory.hasError = false;
            state.createCategory.error = {};
        })
            .addCase(createCategoryAction.fulfilled, (state, action) => {
            state.createCategory.loading = false;
            // @ts-ignore
            state.createCategory.response = action.payload;
            state.createCategory.hasError = false;
            state.createCategory.error = {};
        })
            .addCase(createCategoryAction.rejected, (state, action) => {
            state.createCategory.loading = false;
            state.createCategory.hasError = true;
            state.createCategory.error = action.payload;
        })
            .addCase(updateCategoryAction.pending, (state) => {
            state.updateCategory.loading = true;
            state.updateCategory.response = null;
            state.updateCategory.hasError = false;
            state.updateCategory.error = {};
        })
            .addCase(updateCategoryAction.fulfilled, (state, action) => {
            state.updateCategory.loading = false;
            // @ts-ignore
            state.updateCategory.response = action.payload;
            state.updateCategory.hasError = false;
            state.updateCategory.error = {};
        })
            .addCase(updateCategoryAction.rejected, (state, action) => {
            state.updateCategory.loading = false;
            state.updateCategory.hasError = true;
            state.updateCategory.error = action.payload;
        })
            .addCase(deleteCategory.pending, (state) => {
            state.deleteCategory.loading = true;
            state.deleteCategory.response = null;
            state.deleteCategory.hasError = false;
            state.deleteCategory.error = {};
        })
            .addCase(deleteCategory.fulfilled, (state, action) => {
            state.deleteCategory.loading = false;
            // @ts-ignore
            state.deleteCategory.response = action.payload;
            state.deleteCategory.hasError = false;
            state.deleteCategory.error = {};
        })
            .addCase(deleteCategory.rejected, (state, action) => {
            state.deleteCategory.loading = false;
            state.deleteCategory.hasError = true;
            state.deleteCategory.error = action.payload;
        })
            .addCase(getFloorShapeAction.pending, (state) => {
            state.floorShapes.loading = true;
            state.floorShapes.response = null;
            state.floorShapes.hasError = false;
            state.floorShapes.error = {};
        })
            .addCase(getFloorShapeAction.fulfilled, (state, action) => {
            state.floorShapes.loading = false;
            state.floorShapes.response = action.payload;
            state.floorShapes.hasError = false;
            state.floorShapes.error = {};
        })
            .addCase(getFloorShapeAction.rejected, (state, action) => {
            state.floorShapes.loading = false;
            state.floorShapes.hasError = true;
            state.floorShapes.error = action.payload;
        })
            .addCase(getConnectionsAction.pending, (state) => {
            state.connections.loading = true;
            state.connections.response = null;
            state.connections.hasError = false;
            state.connections.error = {};
        })
            .addCase(getConnectionsAction.fulfilled, (state, action) => {
            state.connections.loading = false;
            state.connections.response = action.payload;
            state.connections.hasError = false;
            state.connections.error = {};
        })
            .addCase(getConnectionsAction.rejected, (state, action) => {
            state.connections.loading = false;
            state.connections.hasError = true;
            state.connections.error = action.payload;
        })
            .addCase(createConnectionAction.pending, (state) => {
            state.createConnections.loading = true;
            state.createConnections.response = null;
            state.createConnections.hasError = false;
            state.createConnections.error = {};
        })
            .addCase(createConnectionAction.fulfilled, (state, action) => {
            state.createConnections.loading = false;
            state.createConnections.response = action.payload;
            state.createConnections.hasError = false;
            state.createConnections.error = {};
        })
            .addCase(createConnectionAction.rejected, (state, action) => {
            state.createConnections.loading = false;
            state.createConnections.hasError = true;
            state.createConnections.error = action.payload;
        })
            .addCase(getCategoriesIconAction.pending, (state) => {
            state.categoryIcons.loading = true;
            state.categoryIcons.response = null;
            state.categoryIcons.hasError = false;
            state.categoryIcons.error = {};
        })
            .addCase(getCategoriesIconAction.fulfilled, (state, action) => {
            state.categoryIcons.loading = false;
            state.categoryIcons.response = action.payload;
            state.categoryIcons.hasError = false;
            state.categoryIcons.error = {};
        })
            .addCase(getCategoriesIconAction.rejected, (state, action) => {
            state.categoryIcons.loading = false;
            state.categoryIcons.hasError = true;
            state.categoryIcons.error = action.payload;
        })
            .addCase(fetchEvents.pending, (state) => {
            state.events.loading = true;
            state.events.response = null;
            state.events.hasError = false;
        })
            .addCase(fetchEvents.fulfilled, (state, action) => {
            state.events.loading = false;
            state.events.response = action.payload;
            state.events.hasError = false;
        })
            .addCase(fetchEvents.rejected, (state, action) => {
            state.events.loading = false;
            state.events.hasError = true;
            state.events.error = action.payload;
        })
            .addCase(createEventAction.pending, (state) => {
            state.createEvent.loading = true;
            state.createEvent.response = null;
            state.createEvent.hasError = false;
        })
            .addCase(createEventAction.fulfilled, (state, action) => {
            state.createEvent.loading = false;
            // state.createEvent.response = action.payload;
            state.createEvent.hasError = false;
        })
            .addCase(createEventAction.rejected, (state, action) => {
            state.createEvent.loading = false;
            state.createEvent.hasError = true;
            state.createEvent.error = action.payload;
        })
            .addCase(updateEventAction.pending, (state) => {
            state.updateEvent.loading = true;
            state.updateEvent.response = null;
            state.updateEvent.hasError = false;
        })
            .addCase(updateEventAction.fulfilled, (state, action) => {
            state.updateEvent.loading = false;
            // state.updateEvent.response = action.payload;
            state.updateEvent.hasError = false;
        })
            .addCase(updateEventAction.rejected, (state, action) => {
            state.updateEvent.loading = false;
            state.updateEvent.hasError = true;
            state.updateEvent.error = action.payload;
        })
            .addCase(deleteEvent.pending, (state) => {
            state.deleteEvent.loading = true;
            state.deleteEvent.response = null;
            state.deleteEvent.hasError = false;
            state.deleteEvent.error = {};
        })
            .addCase(deleteEvent.fulfilled, (state, action) => {
            state.deleteEvent.loading = false;
            // @ts-ignore
            state.deleteEvent.response = action.payload;
            state.deleteEvent.hasError = false;
            state.deleteEvent.error = {};
        })
            .addCase(deleteEvent.rejected, (state, action) => {
            state.deleteEvent.loading = false;
            state.deleteEvent.hasError = true;
            state.deleteEvent.error = action.payload;
        });
    },
});
// Export the action to reset state
export const { resetLocationTypes } = editorSlice.actions;
// Export the reducer
export default editorSlice.reducer;
